import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Store, ArrowRight, ShieldCheck, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e, creds) => {
    if (e) e.preventDefault();
    const c = creds || { email, password };
    setLoading(true);
    try {
      const u = await login(c.email, c.password);
      toast.success(`Bienvenue ${u.name}`);
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0B0F17] text-slate-100">
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-14 relative overflow-hidden border-r border-[#1E293B]">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/11871774/pexels-photo-11871774.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/70 to-transparent" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-amber-500 flex items-center justify-center">
            <Store className="h-6 w-6 text-[#0B0F17]" />
          </div>
          <span className="font-head text-2xl font-extrabold tracking-tight">Brico CRM</span>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-head text-4xl font-extrabold leading-tight tracking-tight">
            Pilotez la <span className="text-amber-500">rentabilité</span> de votre magasin de bricolage.
          </h1>
          <p className="mt-4 text-slate-400 text-base leading-relaxed">
            CRM complet : prestations chiffrées, devis détaillés, pipeline de ventes et analyse
            poussée du chiffre d'affaires face à la marge réalisée en rayon.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-amber-500 flex items-center justify-center">
              <Store className="h-5 w-5 text-[#0B0F17]" />
            </div>
            <span className="font-head text-xl font-extrabold">Brico CRM</span>
          </div>
          <h2 className="font-head text-2xl font-bold">Connexion</h2>
          <p className="text-sm text-slate-400 mt-1">Accédez à votre espace de pilotage.</p>

          <form onSubmit={(e) => submit(e)} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@entreprise.fr"
                className="bg-[#111827] border-[#1E293B] h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Mot de passe</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-[#111827] border-[#1E293B] h-11"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold"
            >
              {loading ? "Connexion…" : "Se connecter"} <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </form>

          <div className="mt-8">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-3">Comptes de démonstration</p>
            <div className="grid gap-3">
              <button
                data-testid="demo-admin-button"
                onClick={() => submit(null, { email: "adrien.mbconception@gmail.com", password: "admin123" })}
                className="flex items-center gap-3 p-3 rounded-xl bg-[#111827] border border-[#1E293B] hover:border-amber-500/40 transition-colors text-left"
              >
                <ShieldCheck className="h-5 w-5 text-amber-500 shrink-0" />
                <div>
                  <div className="text-sm font-semibold">Directeur / Admin</div>
                  <div className="text-xs text-slate-500">Accès total — marges & coûts d'achat</div>
                </div>
              </button>
              <button
                data-testid="demo-commercial-button"
                onClick={() => submit(null, { email: "commercial@batipro.fr", password: "commercial123" })}
                className="flex items-center gap-3 p-3 rounded-xl bg-[#111827] border border-[#1E293B] hover:border-sky-500/40 transition-colors text-left"
              >
                <Briefcase className="h-5 w-5 text-sky-400 shrink-0" />
                <div>
                  <div className="text-sm font-semibold">Chargé de ventes</div>
                  <div className="text-xs text-slate-500">Devis, clients & pipeline</div>
                </div>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
